import { Link } from "react-router-dom";
import HeroCarousel from "@/components/feature/HeroCarousel";
import SectionHeading from "@/components/feature/SectionHeading";
import EpisodeCard from "@/components/feature/EpisodeCard";
import ArticleItem from "@/components/feature/ArticleItem";
import LoadingState from "@/components/base/LoadingState";
import { useSeo } from "@/hooks/useSeo";
import { useMockLoading } from "@/hooks/useMockLoading";
import { getHotEpisodes, getLatestArticles } from "@/lib/content";
import { episodes } from "@/mocks/episodes";
import { articles } from "@/mocks/articles";
import { specials } from "@/mocks/specials";
import { navLinks, quickCategories, siteInfo, siteTdk } from "@/mocks/site";

export default function Home() {
  useSeo(siteTdk.home);
  const loading = useMockLoading(500);

  const featured = getHotEpisodes(6);
  const news = getLatestArticles(5);

  const stats = [
    { icon: "ri-book-open-line", value: `${episodes.length}`, label: "剧情章节" },
    { icon: "ri-newspaper-line", value: `${articles.length}`, label: "资讯文章" },
    { icon: "ri-stack-line", value: `${specials.length}`, label: "深度专题" },
    { icon: "ri-calendar-check-line", value: "每日", label: "持续更新" },
  ];

  return (
    <>
      <HeroCarousel />

      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
        <SectionHeading
          eyebrow="Featured"
          title="最新剧情介绍精选"
          description="精挑近期上线的剧情章节，点击即可进入详情页查看完整剧情脉络。"
          linkTo="/category"
          linkLabel="全部剧情"
        />
        {loading ? (
          <LoadingState text="正在加载剧情精选…" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {featured.map((episode) => (
              <EpisodeCard key={episode.id} episode={episode} />
            ))}
          </div>
        )}
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <SectionHeading
              eyebrow="News"
              title="热门影视资讯"
              description="官方发布、剧情解读与幕后制作动态，掌握第一手消息。"
              linkTo="/special"
              linkLabel="进入专题"
            />
            <div className="rounded-lg bg-background-50 border border-background-200/80 p-2 md:p-3 divide-y divide-background-200/70">
              {news.map((article, idx) => (
                <ArticleItem key={article.id} article={article} index={idx} />
              ))}
            </div>
          </div>

          <div>
            <SectionHeading eyebrow="Chapters" title="剧集快捷入口" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
              {quickCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={cat.path}
                  className="group flex items-center gap-4 p-4 rounded-lg bg-background-50 border border-background-200/80 hover:border-accent-300 transition-colors cursor-pointer"
                >
                  <span className="w-12 h-12 shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 text-2xl group-hover:bg-accent-500 group-hover:text-primary-950 transition-colors">
                    <i className={cat.icon}></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block font-medium text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {cat.label}
                    </span>
                    <span className="block text-xs text-foreground-500 mt-0.5 truncate">
                      {cat.desc}
                    </span>
                  </span>
                  <i className="ri-arrow-right-line ml-auto text-foreground-300 group-hover:text-accent-500 transition-colors"></i>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 font-label text-xs tracking-[0.2em] uppercase text-accent-600 mb-3">
              <span className="w-6 h-px bg-accent-500"></span>
              About the site
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              专注《朋友的妈妈3》的影视内容专站
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              {siteInfo.intro}
            </p>
            <p className="mt-4 text-sm text-foreground-500 leading-relaxed">
              本站内容以可复制的文本形式呈现，方便影迷查阅与交流。搜索、筛选、排序等功能均为前端演示，
              数据为虚构内容，不含播放、下载、会员与支付服务。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line"></i>
                了解本站
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 hover:bg-background-100 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-search-line"></i>
                搜索内容
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 md:gap-5">
            {stats.map((item) => (
              <div
                key={item.label}
                className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/80"
              >
                <span className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 text-2xl mb-4">
                  <i className={item.icon}></i>
                </span>
                <p className="font-label text-2xl md:text-3xl font-semibold text-foreground-950">
                  {item.value}
                </p>
                <p className="mt-1 text-sm text-foreground-500">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pb-16 bg-background-50">
        <div className="rounded-lg bg-primary-950 px-6 py-8 md:px-10 md:py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">
              开始浏览第 {episodes.length} 章剧情介绍
            </h2>
            <p className="mt-2 text-sm text-background-100/70">
              从雨夜归人到天台上的选择，完整梳理这部作品的故事线。
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-book-open-line"></i>
              剧情分类
            </Link>
            <Link
              to={navLinks[2].path}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-50/40 text-background-50 hover:bg-background-50/10 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-newspaper-line"></i>
              资讯专题
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}